import type { ToastService, ToastShowOptions } from '.';
import { optional } from 'inversify';
import { inject, injectable } from 'src/features/ioc';
import type { CurrentUserModel } from 'src/models/CurrentUserModel';
import type { ReactBindings } from 'src/types/bindings';

@injectable()
export class ToastServiceImpl implements ToastService {
    readonly #store: ReactBindings['Store'];
    readonly #currentUserModel: CurrentUserModel | undefined;

    constructor(
        @inject('Store') store: ReactBindings['Store'],
        @inject('CurrentUserModel') @optional() currentUserModel?: CurrentUserModel,
    ) {
        this.#store = store;
        this.#currentUserModel = currentUserModel;
    }

    show({ message, title, variant, group, durationMs, requestId, debugMessage }: ToastShowOptions): string {
        const id = crypto.randomUUID();
        this.#store.dispatch({
            type: 'toast::enqueue',
            toast: {
                id,
                message,
                title,
                variant,
                group: group ?? 'default',
                autoHideDuration: durationMs,
                requestId,
                debugMessage: this.#isDeveloper() ? debugMessage : undefined,
            },
        });
        return id;
    }

    // As `useIsDeveloper` reads it.
    #isDeveloper(): boolean {
        const auth = this.#store.getState().auth;
        return this.#currentUserModel?.isDeveloper.peek() ?? (auth?.masqUser ?? auth?.user)?.isDeveloper ?? false;
    }

    dismiss(id: string): void {
        this.#store.dispatch({ type: 'toast::dismiss', id });
    }
}
